import LoadingComponent from "./LoadingComponent.jsx";
import React, { useMemo, useRef, useEffect } from 'react';
import {createPortal} from 'react-dom';

export function isModalExternalPortalTarget(target) {
    return Boolean(
        target?.id?.startsWith('react-select')
        || target?.closest?.('[class*="__menu-portal"]')
    );
}

export default function Modal({ children, className, visible, setVisible, title, subtitle, loading }) {
    const modalContentRef = useRef(null);

    const handleClickOutside = (e) => {
        if (
            !modalContentRef.current.contains(e.target) &&
            !isModalExternalPortalTarget(e.nativeEvent.target)
        ) {
            onClose();
        }
    };

    const onClose = () => {
        setVisible(false);
    };

    // Add event listener for Esc key
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && visible) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscKey);
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [visible]); // Dependency array includes visible to ensure proper cleanup

    let fullClassName = useMemo(() => {
        return 'modal-dialog relative flex-col flex rounded-xl shadow-lg' + ' ' +
            'bg-white dark:bg-gray-800 w-full lg:w-11/12 m-auto' + ' ' + className;
    }, [className]);

    if (!visible) {
        return null;
    }

    return createPortal(
        (
            <div
                className="modal py-8 px-4 fixed inset-0 z-50 flex overflow-auto bg-black/50"
                onClick={handleClickOutside}
                data-backdrop=""
            >
                <div
                    className={fullClassName}
                    ref={modalContentRef}
                    data-modal-wrapper=""
                >
                    <div className="modal-header px-5 py-3 border-b border-gray-200 dark:border-gray-700/60">
                        <div className="flex justify-between items-center min-h-6">
                            <div className="modal-text">
                                {title && <div className="font-semibold text-black dark:text-gray-100">{title}</div>}
                                {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
                            </div>

                            <button
                                className="ml-auto text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                                onClick={onClose}
                            >
                                <div className="sr-only">Close</div>
                                <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
                                    <path
                                        d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="px-5 py-4">
                        <LoadingComponent loading={loading}>{children}</LoadingComponent>
                    </div>
                </div>
            </div>
        ),
        document.body,
    );
}
